import {
  E_RAND_TYPES, DEAL_TYPES, USE_AREAS, LAND_PURPOSES, FLOOR_TYPES,
  DIRECTION_POINTS, BUILDING_USES, DIRECTIONS, ENTRY_ROADS,
  getLabel, formatPercent, formatAvailableDate, getTypeGroup, getLandPurpose,
} from './constants';

/* ── helpers ── */

function parseExpense(expenseDetail, expenseValue) {
  if (expenseDetail) {
    try {
      const ep = JSON.parse(expenseDetail);
      if (ep.case === 0 || ep.case === 1) {
        if (ep.exist?.value) {
          const price = parseInt(ep.price);
          if (!isNaN(price)) return priceWon(price / 10000);
        }
        return '없음';
      }
      if (ep.case === 2) {
        const kind = ep.kind?.value;
        if (kind === 'fix') {
          const items = ep.fix?.detail || [];
          let total = 0, hasFixed = false, hasVar = false;
          items.forEach(item => {
            if (item.kind === 'fix' && !isNaN(parseInt(item.value))) { total += parseInt(item.value); hasFixed = true; }
            else hasVar = true;
          });
          const priceText = total > 10000 ? priceWon(total / 10000) : `${total}원`;
          return (hasFixed ? priceText : '') + (hasFixed && hasVar ? ' + ' : '') + (hasVar ? '별도 실비' : '');
        } else if (kind) {
          const price = parseInt(ep[kind]?.price);
          if (!isNaN(price)) return priceWon(price / 10000);
        }
      }
    } catch (_) {}
  }
  const n = Number(expenseValue);
  return n > 0 ? priceWon(n) : '없음';
}

function priceWon(만원) {
  const n = Number(만원);
  if (!n || n <= 0) return '-';
  if (n >= 10000) return `${Math.floor(n / 10000).toLocaleString()}억 ${n % 10000 ? `${(n % 10000).toLocaleString()}만원` : ''}`.trim();
  return `${n.toLocaleString()}만원`;
}

function formatCompletionYear(val) {
  if (!val) return '-';
  const s = String(val).replace(/\D/g, '');
  if (s.length >= 8) return `${s.slice(0, 4)}년 ${s.slice(4, 6)}월 ${s.slice(6, 8)}일`;
  if (s.length >= 6) return `${s.slice(0, 4)}년 ${s.slice(4, 6)}월`;
  if (s.length >= 4) return `${s.slice(0, 4)}년`;
  return val;
}

function formatPriceValue(v) {
  if (v == null || v === '') return '-';
  const n = Number(v);
  if (isNaN(n)) return '-';
  return n.toLocaleString();
}

function calcUnitPrice(price, area) {
  const p = Number(price);
  const a = Number(area);
  if (isNaN(p) || isNaN(a) || a === 0) return '-';
  return (p / a).toLocaleString(undefined, { maximumFractionDigits: 2 });
}

function getUnitPriceAreaKey(eRandType) {
  const t = Number(eRandType);
  // 핸드오프 기준: 전용면적(floor_area)
  if ([0, 1, 2, 7, 8, 10, 11, 18, 20].includes(t)) return { key: 'floor_area', label: '전용면적' };
  // 토지면적
  return { key: 'land_area', label: '토지면적' };
}

const v = (val) => (val != null && val !== '' ? val : '-');
export const fmtNum = (val) => {
  if (val == null || val === '' || val === '-') return val;
  const n = Number(String(val).replace(/,/g, ''));
  if (isNaN(n)) return val;
  return n.toLocaleString();
};


function getVal(fields, key, defaultVal) {
  const edited = fields[key + '_edit'];
  return edited !== undefined ? edited : (defaultVal ?? '-');
}


/* ══════════════════════════════════════════════════════════

   ══════════════════════════════════════════════════════════ */

/* ── 매물 주소 ── */

export function buildProfileAddressRows(sale, config) {
  const fields = config?.fields || {};
  return [
    { key: 'address', label: '주소', value: getVal(fields, 'address', sale.address || '-'), span: 12, readOnly: true },
  ];
}

/* ── 가격 정보 — SaleModel PRICE_INFO 기준 ── */

export function buildProfilePriceRows(sale, config) {
  const fields = config?.fields || {};
  const dt = Number(sale.deal_type);
  const t = Number(sale.e_rand_type);
  const unitArea = getUnitPriceAreaKey(t);
  const rows = [];

  // 제곱미터당 단가 (공통 계산)
  const priceForUnit = dt === 0 ? sale.price : sale.deposit;
  const unitPrice = calcUnitPrice(priceForUnit, sale[unitArea.key]);
  const unitPriceRow = {
    key: 'unit_price', label: '제곱미터당 단가',
    value: getVal(fields, 'unit_price', unitPrice),
    unit: '만원/㎡', hint: `(${unitArea.label} 기준)`,
    span: dt === 2 ? 4 : 6, locale: true, included: true,
  };

  if (dt === 0) {
    // 매매: 거래가격 + 단가 한 줄, 현보증금 + 현월세 다음 줄
    rows.push({ key: 'price', label: '거래가격', value: getVal(fields, 'price', formatPriceValue(sale.price)), unit: '만 원', span: 6, locale: true, included: true });
    rows.push(unitPriceRow);
    rows.push({ key: 'curr_deposit', label: '현 보증금', value: getVal(fields, 'curr_deposit', formatPriceValue(sale.curr_deposit)), unit: '만 원', span: 3, locale: true, included: true });
    rows.push({ key: 'curr_monthly', label: '현 월세', value: getVal(fields, 'curr_monthly', formatPriceValue(sale.curr_monthly)), unit: '만 원', span: 3, locale: true, included: true });
  } else if (dt === 1) {
    // 전세: 전세금 + 단가 한 줄
    rows.push({ key: 'price', label: '전세금', value: getVal(fields, 'price', formatPriceValue(sale.deposit)), unit: '만 원', span: 6, locale: true, included: true });
    rows.push(unitPriceRow);
  } else if (dt === 2) {
    // 월세: 보증금 + 월세 + 단가
    rows.push({ key: 'deposit', label: '보증금', value: getVal(fields, 'deposit', formatPriceValue(sale.deposit)), unit: '만 원', span: 4, locale: true, included: true });
    rows.push({ key: 'monthly', label: '월세', value: getVal(fields, 'monthly', formatPriceValue(sale.monthly)), unit: '만 원', span: 4, locale: true, included: true });
    rows.push(unitPriceRow);
  }

  // 권리금 — 상가점포(10), 사무실(11)만
  if ([10, 11].includes(t)) {
    const premVal = sale.premium_type === 1 && sale.premium_value
      ? formatPriceValue(sale.premium_value)
      : (sale.premium_type === 0 ? '없음' : '표시안함');
    rows.push({ key: 'premium', label: '권리금', value: getVal(fields, 'premium', premVal), unit: premVal !== '없음' && premVal !== '표시안함' ? '만 원' : null, span: 3, locale: true, included: true });
  }

  return rows;
}

/* ── PDF 전용: 가격 합쳐서 하나의 full+price 행으로 ── */

export function buildProfilePdfPriceRow(sale, config) {
  const fields = config?.fields || {};
  const dt = Number(sale.deal_type);
  const t = Number(sale.e_rand_type);
  const unitArea = getUnitPriceAreaKey(t);

  const dealLabel = dt === 0 ? '매매가' : dt === 1 ? '전세금' : '월세가';

  let priceVal;
  if (dt === 0) {
    priceVal = `${fmtNum(getVal(fields, 'price', sale.price))}만원`;
  } else if (dt === 1) {
    priceVal = `${fmtNum(getVal(fields, 'price', sale.deposit))}만원`;
  } else {
    priceVal = `${fmtNum(getVal(fields, 'deposit', sale.deposit))}만원 / ${fmtNum(getVal(fields, 'monthly', sale.monthly))}만원`;
  }

  const priceForUnit = dt === 0 ? sale.price : sale.deposit;
  const unitPrice = calcUnitPrice(priceForUnit, sale[unitArea.key]);
  const subVal = getVal(fields, 'unit_price', unitPrice !== '-'
    ? `${unitPrice}만원/㎡ (${unitArea.label} 기준)`
    : `- 만원/㎡`);

  return {
    key: 'price_main',
    label: dealLabel,
    value: priceVal,
    sub: subVal,
    full: true,
    price: true,
  };
}

/* ── 매물 정보 (핸드오프 유형별 4그룹) ── */

export function buildProfileInfoRows(sale, config) {
  const fields = config?.fields || {};
  const t = Number(sale.e_rand_type);
  const rows = [];

  // 매물 유형 (공통)
  rows.push({ key: 'type', label: '매물 유형', value: getVal(fields, 'type', getLabel(t, E_RAND_TYPES)), readOnly: true, included: true });

  if (t === 16) {
    // 핸드오프: 용도지역, 지목, 토지면적, 진입도로(선택), 현재용도(선택), 추천용도(선택)
    rows.push({ key: 'use_area', label: '용도지역', value: getVal(fields, 'use_area', getLabel(Number(sale.use_area), USE_AREAS)), included: true });
    rows.push({ key: 'land_purpose', label: '지목', value: getVal(fields, 'land_purpose', getLandPurpose(sale)), included: true });
    rows.push({ key: 'land_area', label: '토지면적', value: getVal(fields, 'land_area', v(sale.land_area)), unit: '㎡', locale: true, included: true });
    rows.push({ key: 'entry_road', label: '진입도로', value: getVal(fields, 'entry_road', sale.entry_road == 1 ? '있음' : sale.entry_road == 2 ? '없음' : '-'), included: true });
    rows.push({ key: 'curr_usage', label: '현재용도', value: getVal(fields, 'curr_usage', v(sale.curr_usage)), included: true });
    rows.push({ key: 'suggest_usage', label: '추천용도', value: getVal(fields, 'suggest_usage', v(sale.suggest_usage)), included: true });
  } else if ([2, 10, 11, 18, 20].includes(t)) {
    // 핸드오프: 건축물용도, 용도지역, 전용면적, 추천용도(선택), 사용승인일, 현재/전체(층)
    rows.push({ key: 'building_use', label: '건축물용도', value: getVal(fields, 'building_use', getLabel(sale.building_use, BUILDING_USES)), included: true });
    rows.push({ key: 'use_area', label: '용도지역', value: getVal(fields, 'use_area', getLabel(Number(sale.use_area), USE_AREAS)), included: true });
    rows.push({ key: 'supply_area', label: '계약면적', value: getVal(fields, 'supply_area', v(sale.supply_area)), unit: '㎡', locale: true, included: true });
    rows.push({ key: 'floor_area', label: '전용면적', value: getVal(fields, 'floor_area', v(sale.floor_area)), unit: '㎡', locale: true, included: true });
    rows.push({ key: 'suggest_usage', label: '추천용도', value: getVal(fields, 'suggest_usage', v(sale.suggest_usage)), included: true });
    rows.push({ key: 'completion', label: '사용승인일', value: getVal(fields, 'completion', formatCompletionYear(sale.completion_year)), included: true });
    rows.push({ key: 'floor', label: '현재/전체(층)', value: getVal(fields, 'floor', `${sale.floor_cnt ?? '-'}층 / ${sale.floor_total ?? '-'}층`), included: true });
  } else if ([0, 1, 7, 8].includes(t)) {
    // 핸드오프: 건축물용도, 용도지역, 전용면적, 방향+방향기준, 사용승인일, 현재/전체(층)
    rows.push({ key: 'building_use', label: '건축물용도', value: getVal(fields, 'building_use', getLabel(sale.building_use, BUILDING_USES)), included: true });
    rows.push({ key: 'use_area', label: '용도지역', value: getVal(fields, 'use_area', getLabel(Number(sale.use_area), USE_AREAS)), included: true });
    rows.push({ key: 'supply_area', label: '공급면적', value: getVal(fields, 'supply_area', v(sale.supply_area)), unit: '㎡', locale: true, included: true });
    rows.push({ key: 'floor_area', label: '전용면적', value: getVal(fields, 'floor_area', v(sale.floor_area)), unit: '㎡', locale: true, included: true });
    rows.push({ key: 'direction', label: '방향', value: getVal(fields, 'direction', getLabel(sale.direction, ['선택안함','동','서','남','북','남동','남서','북동','북서'])), included: true });
    rows.push({ key: 'direction_point', label: '방향기준', value: getVal(fields, 'direction_point', getLabel(sale.direction_point, DIRECTION_POINTS)), included: true });
    rows.push({ key: 'completion', label: '사용승인일', value: getVal(fields, 'completion', formatCompletionYear(sale.completion_year)), included: true });
    const ft = sale.floor_type;
    const floorVal = ft != null && ft >= 0 ? (FLOOR_TYPES[ft] || '-') : `${sale.floor_cnt ?? '-'}층 / ${sale.floor_total ?? '-'}층`;
    rows.push({ key: 'floor', label: '현재/전체(층)', value: getVal(fields, 'floor', floorVal), included: true });
  } else {
    // 핸드오프 기타건물: 건축물용도, 용도지역, 토지면적, 연면적, 건폐율, 용적률, 사용승인일, 지상/지하
    rows.push({ key: 'building_use', label: '건축물용도', value: getVal(fields, 'building_use', getLabel(sale.building_use, BUILDING_USES)), included: true });
    rows.push({ key: 'use_area', label: '용도지역', value: getVal(fields, 'use_area', getLabel(Number(sale.use_area), USE_AREAS)), included: true });
    rows.push({ key: 'land_area', label: '토지면적', value: getVal(fields, 'land_area', v(sale.land_area)), unit: '㎡', locale: true, included: true });
    rows.push({ key: 'floor_area', label: '연면적', value: getVal(fields, 'floor_area', v(sale.floor_area)), unit: '㎡', locale: true, included: true });
    rows.push({ key: 'bcr', label: '건폐율', value: getVal(fields, 'bcr', v(sale.land_ratio)), unit: '%', included: true });
    rows.push({ key: 'far', label: '용적률', value: getVal(fields, 'far', v(sale.floor_space)), unit: '%', included: true });
    rows.push({ key: 'completion', label: '사용승인일', value: getVal(fields, 'completion', formatCompletionYear(sale.completion_year)), included: true });
    rows.push({ key: 'floor', label: '지상/지하', value: getVal(fields, 'floor', `${sale.floor_cnt ?? '-'}층 / ${sale.under_floor ?? '-'}층`), included: true });
  }

  return rows;
}

/* ══════════════════════════════════════════════════════════

   ══════════════════════════════════════════════════════════ */

export function buildLandRows(sale, config) {
  const fields = config?.fields || {};
  return [
    { key: 'land_area', label: '면적', value: getVal(fields, 'land_area', v(sale.land_area)), unit: '㎡', locale: true, included: true },
    { key: 'land_purpose', label: '지목', value: getVal(fields, 'land_purpose', getLandPurpose(sale)), included: true },
    { key: 'use_area', label: '용도지역', value: getVal(fields, 'use_area', getLabel(Number(sale.use_area), USE_AREAS) !== '-' ? getLabel(Number(sale.use_area), USE_AREAS) : (sale.use_area_text || '-')), included: true },
    { key: 'land_usage', label: '이용상황', value: getVal(fields, 'land_usage', sale.land_usage || '-'), included: true },
    { key: 'entry_road', label: '도로접면', value: getVal(fields, 'entry_road', sale.entry_road == 1 ? '있음' : sale.entry_road == 2 ? '없음' : (sale.entry_road_text || '-')), included: true },
    { key: 'land_height', label: '지형높이', value: getVal(fields, 'land_height', sale.land_height || '-'), included: true },
    { key: 'land_shape', label: '지형형상', value: getVal(fields, 'land_shape', sale.land_shape || '-'), included: true },
    { key: 'official_land_price', label: '공시지가',
      value: getVal(fields, 'official_land_price', v(sale.official_land_price)),
      unit: '원/㎡', locale: true,
      sub: sale.official_land_price_year ? `${sale.official_land_price_year}년 기준` : null,
      included: true },
  ];
}

/* ══════════════════════════════════════════════════════════

   ══════════════════════════════════════════════════════════ */

export function buildBasicRows(sale, config) {
  const fields = config?.fields || {};
  const t = Number(sale.e_rand_type);
  const dt = Number(sale.deal_type);
  const rows = [];

  if (t === 16) return rows; // 토지: 기본정보 없음

  // ── FLOOR_INFO ──
  if ([0, 1, 2, 7, 8, 20].includes(t) || (t === 4 && dt !== 0)) {
    rows.push({ key: 'floor', label: '층수', value: getVal(fields, 'basic_floor', sale.floor_type != null ? getLabel(sale.floor_type, FLOOR_TYPES) : `${sale.floor_cnt ?? '-'}층 / 총 ${sale.floor_total ?? '-'}층`), included: true });
  } else if ([3, 5, 6, 12, 14, 15, 17, 19].includes(t) || (t === 4 && dt === 0)) {
    rows.push({ key: 'floor', label: '지상 / 지하', value: getVal(fields, 'basic_floor', `${sale.floor_cnt ?? '-'}층 / ${sale.under_floor ?? '-'}층`), included: true });
  } else if ([10, 11, 18].includes(t)) {
    rows.push({ key: 'floor', label: '해당층 / 전체층', value: getVal(fields, 'basic_floor', `${sale.floor_cnt ?? '-'}층 / ${sale.floor_total ?? '-'}층`), included: true });
  }

  // ── DIRECTION_INFO ──
  if ([0, 1, 2, 3, 4, 5, 6, 7, 8].includes(t)) {
    rows.push({ key: 'direction', label: '방향', value: getVal(fields, 'basic_direction', getLabel(sale.direction, DIRECTIONS)), included: true });
    rows.push({ key: 'direction_point', label: '방향 기준', value: getVal(fields, 'basic_direction_point', getLabel(sale.direction_point, DIRECTION_POINTS)), included: true });
  } else {
    rows.push({ key: 'direction', label: '방향(주출입구)', value: getVal(fields, 'basic_direction', getLabel(sale.direction, DIRECTIONS)), included: true });
  }

  // ── STRUCTURE_INFO ──
  rows.push({ key: 'room', label: '방 / 욕실(화장실) 수', value: getVal(fields, 'basic_room', `${sale.room_cnt ?? '-'} / ${sale.bathroom_cnt ?? '-'}개`), included: true });

  // ── DETAIL_INFO (유형별) ──
  // 입주가능일 — 모든 비토지 유형
  rows.push({ key: 'available_date', label: '입주가능일', value: getVal(fields, 'basic_available_date', formatAvailableDate(sale.available_date_value, sale.date_discussable)), included: true });

  // 관리비 — 모든 비토지 유형
  rows.push({ key: 'expense', label: '관리비', value: getVal(fields, 'basic_expense', parseExpense(sale.expense_detail, sale.expense_value)), included: true });

  // 현재용도/추천용도 — 상가(10), 사무실(11)만
  if ([10, 11].includes(t)) {
    rows.push({ key: 'curr_usage', label: '현재용도', value: getVal(fields, 'basic_curr_usage', v(sale.curr_usage)), included: true });
    rows.push({ key: 'suggest_usage', label: '추천용도', value: getVal(fields, 'basic_suggest_usage', v(sale.suggest_usage)), included: true });
  }

  // 사용전력 — 지식산업센터(18)만, API 값이 없으면 표시 안 함
  if (t === 18 && sale.electric_type != null) {
    rows.push({ key: 'electric_type', label: '사용전력', value: getVal(fields, 'basic_electric_type', v(sale.electric_type)), included: true });
  }

  return rows;
}

/* ══════════════════════════════════════════════════════════

   ══════════════════════════════════════════════════════════ */

export function buildBuildingRows(sale, config) {
  const fields = config?.fields || {};
  const t = Number(sale.e_rand_type);
  const dt = Number(sale.deal_type);
  const rows = [];

  if (t === 16) return rows; // 토지: 건물정보 없음

  // fullBuilding: 사용승인일, 용도지역, 용적률, 건폐율, 주구조, 건물용도, 건축면적, 주차
  const fullBuilding = [3, 5, 6, 12, 14, 15, 17, 19].includes(t) || (t === 4 && dt === 0);
  // simple: 사용승인일, 건물용도, 주차
  // 지식산업센터(18): 사용승인일, 용도지역, 주구조, 건물용도, 주차

  // 사용승인일 — 공통
  rows.push({ key: 'completion', label: '사용승인일', value: getVal(fields, 'bldg_completion', formatCompletionYear(sale.completion_year)), included: true });

  if (fullBuilding) {
    rows.push({ key: 'use_area', label: '용도지역', value: getVal(fields, 'bldg_use_area', getLabel(Number(sale.use_area), USE_AREAS)), included: true });
    rows.push({ key: 'floor_space', label: '용적률', value: getVal(fields, 'bldg_floor_space', v(sale.floor_space)), unit: '%', included: true });
    rows.push({ key: 'land_ratio', label: '건폐율', value: getVal(fields, 'bldg_land_ratio', v(sale.land_ratio)), unit: '%', included: true });
    rows.push({ key: 'structure', label: '주구조', value: getVal(fields, 'bldg_structure', sale.structure_type || '-'), included: true });
    rows.push({ key: 'building_use', label: '건물용도', value: getVal(fields, 'bldg_building_use', getLabel(sale.building_use, BUILDING_USES)), included: true });
    rows.push({ key: 'building_area', label: '건축면적', value: getVal(fields, 'bldg_building_area', v(sale.building_area)), unit: '㎡', locale: true, included: true });
    rows.push({ key: 'car', label: '주차', value: getVal(fields, 'bldg_car', sale.car_cnt != null && sale.car_cnt !== '' ? `${sale.car_cnt}대` : '-'), included: true });
  } else if (t === 18) {
    // 지식산업센터
    rows.push({ key: 'use_area', label: '용도지역', value: getVal(fields, 'bldg_use_area', getLabel(Number(sale.use_area), USE_AREAS)), included: true });
    rows.push({ key: 'structure', label: '주구조', value: getVal(fields, 'bldg_structure', sale.structure_type || '-'), included: true });
    rows.push({ key: 'building_use', label: '건물용도', value: getVal(fields, 'bldg_building_use', getLabel(sale.building_use, BUILDING_USES)), included: true });
    rows.push({ key: 'car', label: '주차', value: getVal(fields, 'bldg_car', sale.car_cnt != null && sale.car_cnt !== '' ? `${sale.car_cnt}대` : '-'), included: true });
  } else {
    // simple: 아파트(0,1), 오피스텔(2), 연립(7), 다세대(8), 상가(10), 사무실(11), 다가구(4 전세/월세)
    rows.push({ key: 'building_use', label: '건물용도', value: getVal(fields, 'bldg_building_use', getLabel(sale.building_use, BUILDING_USES)), included: true });
    rows.push({ key: 'car', label: '주차', value: getVal(fields, 'bldg_car', sale.car_cnt != null && sale.car_cnt !== '' ? `${sale.car_cnt}대` : '-'), included: true });
  }

  // 위반건축물여부 — 공통 (O/X 텍스트)
  rows.push({
    key: 'illegal', label: '위반건축물여부',
    value: getVal(fields, 'bldg_illegal', sale.is_illegal_building ? 'O' : 'X'),
    included: true,
  });

  return rows;
}

/* ── 세부정보 — 토지(16)만. SaleModel DETAIL_INFO 토지 케이스 ── */

export function buildDetailRows(sale, config) {
  const fields = config?.fields || {};
  const t = Number(sale.e_rand_type);
  if (t !== 16) return []; // 토지만

  return [
    { key: 'use_area', label: '용도지역', value: getVal(fields, 'detail_use_area', getLabel(Number(sale.use_area), USE_AREAS)), included: true },
    { key: 'land_purpose', label: '지목', value: getVal(fields, 'detail_land_purpose', getLandPurpose(sale)), included: true },
    { key: 'entry_road', label: '진입도로', value: getVal(fields, 'detail_entry_road', sale.entry_road == 1 ? '있음' : sale.entry_road == 2 ? '없음' : '-'), included: true },
    { key: 'curr_usage', label: '현재용도', value: getVal(fields, 'detail_curr_usage', v(sale.curr_usage)), included: true },
    { key: 'suggest_usage', label: '추천용도', value: getVal(fields, 'detail_suggest_usage', v(sale.suggest_usage)), included: true },
  ];
}

/* ══════════════════════════════════════════════════════════

   ══════════════════════════════════════════════════════════ */

function formatPhone(num) {
  if (!num) return '';
  const s = String(num).replace(/[^0-9]/g, '');
  if (s.length === 11) return `${s.slice(0,3)}-${s.slice(3,7)}-${s.slice(7)}`;
  if (s.length === 10) return `${s.slice(0,3)}-${s.slice(3,6)}-${s.slice(6)}`;
  if (s.length === 9) return `${s.slice(0,2)}-${s.slice(2,5)}-${s.slice(5)}`;
  return num;
}

export function buildContactRows(agent, sale, config) {
  const fields = config?.fields || {};
  return {
    office: [
      { key: 'office_name', label: '상호명', value: getVal(fields, 'office_name', agent?.office_name || ''), span: 6 },
      { key: 'company_address', label: '소재지', value: getVal(fields, 'company_address', agent?.company_address || ''), span: 6 },
      { key: 'registration_number', label: '등록번호', value: getVal(fields, 'registration_number', agent?.registration_number || ''), span: 4 },
      { key: 'representative', label: '대표', value: getVal(fields, 'representative', agent?.representative || ''), span: 4 },
      { key: 'phone', label: '대표 연락처', value: formatPhone(getVal(fields, 'phone', agent?.phone || '')), span: 4, inputType: 'tel' },
    ],
    person: [
      { key: 'name', label: '담당자', value: getVal(fields, 'name', [agent?.name, agent?.position].filter(Boolean).join(' ') || ''), span: 6 },
      { key: 'contact_phone', label: '연락처', value: formatPhone(getVal(fields, 'contact_phone', sale?.contact_phone || agent?.phone || '')), span: 6, inputType: 'tel' },
    ],
  };
}

/* ── PDF helper: resolve toggleBtn value to display text ── */

export function resolveToggleBtnLabel(row) {
  if (row.type !== 'toggleBtn' || !row.options) return row.value;
  const match = row.options.find((o) => String(o.value) === String(row.value));
  return match ? match.label : (row.value != null ? String(row.value) : '-');
}
